<script setup>
import { computed } from 'vue'
import { centsToDollars } from './../../../../utils/format-price'
import { trialDays } from './../../../../configuration'

const props = defineProps({
    plan: {
        type: Object,
        default: () => ({}),
    },
    currentSubscription: {
        type: Object,
        default: () => ({}),
    },
    isLoggedIn: {
        type: Boolean,
        default: false,
    },
    isEntitledFreeTrial: {
        type: Boolean,
        default: false,
    },
})

defineEmits(['onAction'])

const isCurrentPlan = computed(() => {
    if (!props.currentSubscription) return false
    return props.plan?.id === props.currentSubscription?.subscriptionsProductsId.id
})

function actionText(durationTrialDays) {
    // Active plan wins on both cards (monthly and yearly).
    if (isCurrentPlan.value) return 'Your Active Plan'
    // Special case for yearly plan
    if (props.plan.durationMonths === 12) return 'Get 2 months free'
    if (!props.isLoggedIn) return 'Go Premium'
    if (props.isEntitledFreeTrial && durationTrialDays) return 'Start free trial'
    return 'Go Premium'
}
</script>

<template>
    <div :class="['tile', { 'yearly-tile': plan.durationMonths === 12 }]">
        <div
            v-if="plan.durationMonths === 12"
            class="popular-badge"
        >Most Popular</div>
        <BaseSection>
            <h2 class="title">{{ plan.title }}</h2>
            <h3 class="price">{{ centsToDollars(plan.price / plan.durationMonths) }} / month <span v-if="plan.durationMonths === 12">(Save 20%!)</span></h3>
            <p
                v-if="plan.durationMonths === 12"
                class="subtitle"
            >
                {{ centsToDollars(plan.price) }} billed annually.
            </p>
            <ul class="sales-points">
                <li class="sales-point">Hi-Fi sound</li>
                <li class="sales-point">No ads</li>
                <li class="sales-point">Stream radio channels in external players and devices</li>
                <li class="sales-point">Download mixes, listen offline on mobile</li>
                <li class="sales-point">VIP access: skip the line and access new mixes before everyone else</li>
                <br v-if="!isLoggedIn && plan.durationMonths === 1">
                <li
                    v-if="!isLoggedIn && plan.durationMonths === 1"
                    class="sales-point"
                >{{ trialDays }}-day free trial (if you haven't yet tried Premium).</li>
            </ul>
            <div class="action">
                <BaseButton
                    :button-type="plan.current ? 'tag' : 'highlight'"
                    :modifiers="{ fill: true, noMargins: true }"
                    :disabled="isCurrentPlan"
                    :aria-disabled="plan.current"
                    class="action-button"
                    @click="$emit('onAction', plan)"
                >{{ actionText(plan.durationTrialDays) }}</BaseButton>
                <p class="disclaimer">No strings attached. Cancel anytime.</p>
            </div>
        </BaseSection>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.tile {
    position: relative;
    width: 330px;
    min-height: 425px;
    padding: 0 $s-xl;
    text-align: left;
    background: $c-surface-raised;
    border-radius: $radius-8;

    &:nth-of-type(1) {
        background: $c-alt-bg;
    }
}

.yearly-tile {
    border: 2px solid $c-highlight;
}

.popular-badge {
    position: absolute;
    top: -$s-xs;
    left: 50%;
    z-index: 1;
    padding: $s-xs $s-l;
    font-size: 12px;
    font-weight: 600;
    color: $c-white;
    letter-spacing: 1px;
    background: $c-highlight;
    border-radius: $radius-8;
    transform: translateX(-50%);
}

.title {
    margin: 0 0 $s-s;
    font-size: 28px;
    font-weight: 400;
    line-height: 24px;
}

.price {
    margin: 0 0 $s-xxs;
    font-size: 18px;
    font-weight: 300;
    line-height: 1;
}

.subtitle {
    @extend %contentSubtitleS;

    min-width: 100%;
    margin: 0;
}

.action {
    position: absolute;
    right: 0;
    bottom: $s-xl;
    left: 0;
    width: 100%;
    padding: 0 $s-l;
    margin: 0 auto;

    .action-button {
        font-weight: 600;
    }
}

.disclaimer {
    @extend %contentSubtitleM;

    margin: $s-s 0 0 0;
    font-size: 12px;
    line-height: 1.2;
    color: $c-text-faded;
    text-align: center;
}

.sales-points {
    @extend %contentSubtitleM;

    padding: 0;
    margin-top: $s-xl;
    list-style: none;
}

.sales-point {
    font-size: 13px;
    font-weight: 300;
    line-height: 1.2;
}

.sales-point ~ .sales-point {
    margin-top: $s-s;
}

@include tablet {
    .tile {
        padding-top: $s-xl;
    }

    .sales-points {
        margin-top: $s-xxl;
    }
}
</style>
